import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress } from "@mui/material";
import { isMobile } from "react-device-detect";
import cx from "classnames";

import { BYTES_IN_MEGABYTE } from "../../../../constants";
import { supportedDocsMsg } from "../../../../constants/documents";
import { DocumentUploadError } from "../../../../components/DocumentUploadError/DocumentUploadError";
import { documentValidationSchema } from "../../../../utils/validation";
import { Icon, ICONS } from "../../../../components/Icons";
import { ContexualHelp } from "../../../../components/Notifications";
import { ReactComponent as FileIconSvg } from "../../../../assets/icons/fileUpload.svg";
import { ReactComponent as Check } from "../../../../assets/icons/loadingGreen.svg";
import { ReactComponent as AlertIcon } from "../../../../assets/icons/alert.svg";
import { OverwriteAlert } from "../OverwriteAlert/OverwriteAlert";

import { StyledUploadContainer } from "../styled";

/**
 * Component for rendering a document row with upload functionality
 *
 * @component
 */
export const DocumentRowComponent = ({
  isDisabledUploadForRO,
  identifier,
  isUploaded,
  selectedFile,
  isUploading,
  percentComplete,
  reUploadHandler,
  cancelHandler,
  isUploadError,
  uploadDocument,
  document,
  docRemoveWarning = true,
  infoMessage = "",
  multiDoc = false,
  required = false,
  displayValidationMsg = false,
  contextualHelpProps,
  fieldDescription
}) => {
  const inputEl = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [removeAlert, setRemoveAlert] = useState(false);
  const [confirmAlert, setConfirmAlert] = useState(false);

  /**
   * Handles file input change
   * @param {Event} event - The change event from file input
   */
  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      try {
        setErrorMessage(null);
        documentValidationSchema.validateSync({ file }, { abortEarly: false });
        uploadDocument(file);
      } catch (error) {
        setErrorMessage(error.message);
      }
    }
  };
  /**
   * Handles click on the upload container
   * @param {React.MouseEvent} event - The click event
   */
  const handleContainerClick = event => {
    // Skip if the click was on or inside a button element to prevent duplicate handling
    if (event && (event.target.closest("button") || event.target.tagName === "BUTTON")) {
      return;
    }

    if (!isUploaded && !isDisabledUploadForRO && !isUploading && inputEl.current) {
      inputEl.current.click();
    }
  };

  /**
   * Handles retry action for failed uploads
   */
  const tryAgain = () => {
    inputEl.current.click();
    reUploadHandler();
  };

  /**
   * Handles cancellation of upload with confirmation
   */
  const cancelUpload = () => {
    cancelHandler();
    setRemoveAlert(false);
    if (docRemoveWarning) inputEl.current.click();
  };

  /**
   * Handles confirmed cancellation of upload
   */
  const cancelUploadConfirm = () => {
    cancelHandler();
    setConfirmAlert(false);
  };

  // Create a variable for hasFile to match Upload.js pattern
  const hasFile = isUploaded || selectedFile;

  /**
   * Renders the appropriate icon based on document state
   * @returns {JSX.Element} The icon component
   */
  const renderIcon = () => {
    if (hasFile) {
      return <Check height="24px" width="24px" className="success" />;
    } else if (isUploadError && typeof isUploadError !== "undefined") {
      return <AlertIcon height="24px" width="24px" alt="errorIcon" className="errorIcon" />;
    } else {
      return (
        <FileIconSvg
          height={isMobile ? "35px" : "44px"}
          width={isMobile ? "32px" : "40px"}
          alt="documentIcon"
          className="fileIcon"
        />
      );
    }
  };

  return (
    <>
      <StyledUploadContainer data-testid={`document-row-${identifier}`}>
        {fieldDescription && (
          <ContexualHelp title={infoMessage} {...contextualHelpProps}>
            <div className="fieldDescription">{fieldDescription}</div>
          </ContexualHelp>
        )}{" "}
        <div
          className={cx(
            "uplaodContainer",
            { "Mui-error": !!errorMessage || displayValidationMsg },
            { disabledUpload: isDisabledUploadForRO }
          )}
          onClick={event => handleContainerClick(event)}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleContainerClick(event);
            }
          }}
          role="button"
          tabIndex={isDisabledUploadForRO ? -1 : 0}
          aria-disabled={isDisabledUploadForRO || isUploading}
          aria-label={hasFile ? "View uploaded document" : "Upload document"}
        >
          <div className="main">
            <div className="row">{renderIcon()}</div>
            <div className="contentContainer">
              <section>
                <div className="contentWrapper">
                  <input
                    name="file"
                    type="file"
                    data-testid="fileInput"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    disabled={isDisabledUploadForRO || isUploading}
                    ref={inputEl}
                  />
                  <div className="content" data-testid={`document-content-${identifier}`}>
                    {/* Conditional rendering for document content */}
                    {hasFile ? (
                      <p data-testid="uploadedFileName">
                        {document.fileDescription ||
                          (selectedFile && !multiDoc && selectedFile.name) ||
                          document.documentTitle}
                      </p>
                    ) : (
                      <>
                        {document.documentTitle ??
                          "Drag and drop file here or upload from your computer"}
                      </>
                    )}
                  </div>{" "}
                  {(() => {
                    // Using IIFE to replace nested ternary for better readability
                    // This approach follows SonarCloud guidelines for conditional rendering
                    if (hasFile && !isUploading) {
                      return (
                        <div className="subcontent">
                          {(document.fileSize / BYTES_IN_MEGABYTE).toFixed(2)} MB
                        </div>
                      );
                    }

                    if (isUploadError && typeof isUploadError !== "undefined") {
                      return <DocumentUploadError tryAgainHandler={tryAgain} />;
                    }

                    if (isUploading) {
                      return (
                        <div className="row">
                          <CircularProgress size="14px" className="isUploadingStatus" />
                          <div className="subcontent">Uploading...</div>
                        </div>
                      );
                    }

                    return <div className="subcontent">{supportedDocsMsg}</div>;
                  })()}
                  {infoMessage && !isUploaded && (
                    <ContexualHelp
                      title={infoMessage}
                      placement="right"
                      isDisableHoverListener={false}
                    >
                      <div className="row">
                        <Icon name={ICONS.question} className="uploadInfoIcon" alt="question" />
                        <p>Need more information?</p>
                      </div>
                    </ContexualHelp>
                  )}
                  {errorMessage && <div className={cx("subcontent", "error")}>{errorMessage}</div>}
                  {displayValidationMsg && !isUploaded && (
                    <DocumentUploadError
                      error={document.documentTitle + " is required"}
                      tryAgain={false}
                    />
                  )}
                </div>
              </section>
            </div>
          </div>

          {!isDisabledUploadForRO && (
            <>
              {hasFile ? (
                <Button
                  color="primary"
                  variant="outlined"
                  className="actionButton"
                  onClick={event => {
                    event.stopPropagation();
                    docRemoveWarning ? setRemoveAlert(true) : setConfirmAlert(true);
                  }}
                  data-testid="removeButton"
                >
                  Remove
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  className="actionButton"
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation(); // Stop event propagation to container
                    if (inputEl.current) {
                      inputEl.current.click();
                    }
                  }}
                  data-testid="uploadFileButton"
                  disabled={isDisabledUploadForRO || isUploading}
                >
                  Upload
                </Button>
              )}
            </>
          )}
        </div>
      </StyledUploadContainer>

      {removeAlert && (
        <OverwriteAlert
          message="We can see that you are about to re-upload a document. Keep in mind that this will replace your previous upload"
          handleClose={() => setRemoveAlert(false)}
          isOpen={removeAlert}
          handleConfirm={cancelUpload}
        />
      )}
      {confirmAlert && (
        <OverwriteAlert
          message="Are you sure you want to remove this upload?"
          handleClose={() => setConfirmAlert(false)}
          isOpen={confirmAlert}
          handleConfirm={cancelUploadConfirm}
        />
      )}
    </>
  );
};

/**
 * PropTypes for DocumentRowComponent
 */
DocumentRowComponent.propTypes = {
  /**
   * Flag indicating if upload should be disabled for RO
   */
  isDisabledUploadForRO: PropTypes.bool,

  /**
   * Unique identifier for the document row
   */
  identifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /**
   * Flag indicating if document is already uploaded
   */
  isUploaded: PropTypes.bool,

  /**
   * The currently selected file object
   */
  selectedFile: PropTypes.object,

  /**
   * Flag indicating if a document upload is in progress
   */
  isUploading: PropTypes.bool,

  /**
   * Percentage of upload completion
   */
  percentComplete: PropTypes.number,

  /**
   * Handler function for re-uploading a document
   */
  reUploadHandler: PropTypes.func,

  /**
   * Handler function for cancelling upload
   */
  cancelHandler: PropTypes.func,

  /**
   * Flag or error message for upload errors
   */
  isUploadError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * Handler function for uploading a document
   */
  uploadDocument: PropTypes.func.isRequired,

  /**
   * Document object containing metadata
   */
  document: PropTypes.shape({
    /**
     * Title of the document
     */
    documentTitle: PropTypes.string.isRequired,

    /**
     * File description if available
     */
    fileDescription: PropTypes.string,

    /**
     * Size of the file
     */
    fileSize: PropTypes.number
  }).isRequired,

  /**
   * Whether to show warning when removing document
   */
  docRemoveWarning: PropTypes.bool,

  /**
   * Additional information message for the document
   */
  infoMessage: PropTypes.string,

  /**
   * Whether this is a multi-document upload component
   */
  multiDoc: PropTypes.bool,

  /**
   * Whether the document is required
   */
  required: PropTypes.bool,

  /**
   * Whether to display validation messages
   */
  displayValidationMsg: PropTypes.bool,

  /**
   * Props for the contextual help component
   */
  contextualHelpProps: PropTypes.object,

  /**
   * Field description text
   */
  fieldDescription: PropTypes.node
};
